import type {
  AnalysisState,
  BoardCommunity,
  BoardView,
  ModalitaTempoSfida,
  OriginPlayer,
  OriginResult,
  OriginTimeControl,
  PositionView,
  ScenarioView,
} from '@/types/myBoards';

/**
 * 🔴 DATI FINTI del T0 di «My Boards» (tma-orcb-25, ADR-0191): niente rete, niente QGammon. Servono a disegnare la lista,
 * la scheda e il foglio della sfida sulle FORME del contratto (`types/myBoards.ts`), con la flag spenta in ogni file env.
 * Il T1 li sostituisce con le rotte vere (Q2/Q3, scritte da qgammon-orcr dal 06/10): nessun componente li importa, li legge
 * SOLO `hooks/useMyBoards.ts` — il punto unico in cui, al T1, entrano le chiamate.
 *
 * Le posizioni rispettano la convenzione di `qgammon-rules` (indice i = punta i+1, positivo Bianco, negativo Nero) e le
 * pedine tornano a 15 per lato (in gioco + barra + uscite): `tests/my-boards-dati-finti.test.ts` lo verifica COL MOTORE, e
 * verifica che il pip dichiarato in `stats` sia quello che il motore calcola. ⛔ L'aritmetica NON si ricopia qui: `stats` e' un
 * valore finto per posizione, della forma di `positionStats` (Q3).
 */

/** L'utente «che guarda» nei dati finti: il suo `userId` e' quello che la lista scrive «Tu». */
export const MIO_ID_FINTO = 'a11c0000-0000-4000-8000-0000000000aa';

/** 64 esadecimali derivati dall'id: uguali a se stessi, diversi fra posizioni. */
const sha = (id: string, riempi: string): string => (id.replace(/[^0-9a-f]/g, '') + riempi.repeat(64)).slice(0, 64);

const vuote = (): number[] => Array.from({ length: 24 }, () => 0);

/** Costruisce le 24 punte da coppie `[punta 1..24, pedine con segno]`. */
function punte(...celle: [number, number][]): number[] {
  const p = vuote();
  for (const [punta, n] of celle) p[punta - 1] = n;
  return p;
}

const giocatore = (userId: string | null, displayName: string, elo: number, isBot = false): OriginPlayer => ({
  userId,
  displayName,
  avatarUrl: null,
  elo,
  isBot,
});

const BLITZ_3_10: OriginTimeControl = { bankSeconds: 180, incrementSeconds: 10 };
const BLITZ_1_5: OriginTimeControl = { bankSeconds: 60, incrementSeconds: 5 };
const SENZA_OROLOGIO: OriginTimeControl = { bankSeconds: 0, incrementSeconds: 0, unlimited: true };

const esito = (r: Partial<OriginResult> & Pick<OriginResult, 'winner' | 'score'>): OriginResult => ({
  status: 'completed',
  winMagnitude: 1,
  endedAt: '2026-10-04T12:00:00.000Z',
  endReason: null,
  abandonedReason: null,
  ...r,
});

/** La posizione di partenza del backgammon. */
const PARTENZA = punte([24, 2], [13, 5], [8, 3], [6, 5], [1, -2], [12, -5], [17, -3], [19, -5]);

/** Una posizione di gioco avanzato: il Nero ha una pedina sulla barra. Pip 129 contro 137. */
const MEDIO_GIOCO = punte([13, 3], [24, 1], [8, 3], [6, 4], [5, 2], [4, 2], [17, -3], [19, -3], [20, -2], [21, -2], [12, -4]);

/** Un finale di corsa: quattro uscite per lato, pip 55 contro 55. */
const FINALE = punte([6, 5], [5, 3], [4, 2], [2, 1], [19, -5], [20, -3], [21, -2], [23, -1]);

function posizione(p: Partial<PositionView> & Pick<PositionView, 'id' | 'points'>): PositionView {
  return {
    v: 1,
    rulesVersion: 'rules-0.1.0',
    // Due sha256 (64 esadecimali): la forma che `PositionViewSchema` pretende. Valori di fixture, non hash di una storia vera.
    hash: sha(p.id, 'a'),
    historyHash: sha(p.id, 'b'),
    playable: true,
    bar: { W: 0, B: 0 },
    off: { W: 0, B: 0 },
    turn: 'W',
    phase: 'rolling',
    roll: null,
    cube: { value: 1, owner: null, known: true },
    crawford: false,
    match: { length: 1, scoreW: 0, scoreB: 0, gameNumber: 1 },
    ...p,
  };
}

/** Il medio gioco, di turno il Nero col 6-2 in mano e il cubo a 2 del Bianco (la tavola T5). */
const POS_MEDIO_GIOCO = posizione({
  id: 'b05e0000-0000-4000-8000-000000000001',
  points: MEDIO_GIOCO,
  bar: { W: 0, B: 1 },
  turn: 'B',
  phase: 'moving',
  roll: [6, 2],
  cube: { value: 2, owner: 'W', known: true },
  match: { length: 5, scoreW: 3, scoreB: 2, gameNumber: 2 },
});

const POS_PARTENZA = posizione({ id: 'b05e0000-0000-4000-8000-000000000002', points: PARTENZA });

const POS_FINALE = posizione({
  id: 'b05e0000-0000-4000-8000-000000000003',
  points: FINALE,
  off: { W: 4, B: 4 },
  turn: 'W',
  // Partita vecchia, prima del cubo per ply (ADR-0191 §7): il cubo e' «ignoto».
  // Cubo ignoto: `value` e `owner` sono `null`, MAI un 1 inventato (`PositionViewSchema`).
  cube: { value: null, owner: null, known: false },
  match: { length: 3, scoreW: 0, scoreB: 0, gameNumber: 1 },
});

/** Un raddoppio offerto: da giocare solo come disposizione, perche' l'offerta e' tolta dallo scenario. */
const POS_RADDOPPIO = posizione({
  id: 'b05e0000-0000-4000-8000-000000000004',
  points: MEDIO_GIOCO,
  bar: { W: 0, B: 1 },
  turn: 'B',
  phase: 'cubeOffered',
  cube: { value: 1, owner: null, known: true },
  match: { length: 5, scoreW: 3, scoreB: 2, gameNumber: 2 },
});

const QUANDO = (giorniFa: number, ora: number, minuto: number): string =>
  new Date(Date.UTC(2026, 9, 4 - giorniFa, ora, minuto)).toISOString();

/**
 * I board di prova, dal piu' recente (la lista e' privata e il piu' recente sta in cima, T4). Sono in ordine di creazione,
 * cosi' `useMyBoards` non deve riordinare: lo fa il server.
 */
export const BOARD_FINTI: BoardView[] = [
  {
    // T4/T5b: guardata dal vivo, in un torneo. Le stime sono chiuse (`source_live`).
    id: 'b0a4d000-0000-4000-8000-000000000001',
    capturedFrom: 'spectator',
    createdAt: QUANDO(0, 14, 2),
    clocksAtCapture: { W: 102_000, B: 125_000 },
    position: POS_MEDIO_GIOCO,
    origin: {
      matchId: '3a7c0000-0000-4000-8000-000000000001',
      gameNumber: 2,
      ply: 14,
      status: 'live',
      players: { W: giocatore('a11c0000-0000-4000-8000-000000000001', 'Giulia R.', 1655), B: giocatore('a11c0000-0000-4000-8000-000000000002', 'Marco_bg', 1612) },
      timeControl: BLITZ_3_10,
      tournamentMatchId: 'tm-77',
      serviceOrdered: true,
      rated: false,
      result: null,
    },
  },
  {
    // T4: giocata in pratica contro un bot, la partenza.
    id: 'b0a4d000-0000-4000-8000-000000000002',
    capturedFrom: 'player',
    createdAt: QUANDO(1, 18, 30),
    clocksAtCapture: null,
    position: POS_PARTENZA,
    origin: {
      matchId: '3a7c0000-0000-4000-8000-000000000002',
      gameNumber: 1,
      ply: 1,
      status: 'live',
      players: { W: giocatore(MIO_ID_FINTO, 'Io', 1500), B: giocatore('a11c0000-0000-4000-8000-0000000000b1', 'Bot Facile 1', 800, true) },
      timeControl: SENZA_OROLOGIO,
      tournamentMatchId: null,
      serviceOrdered: false,
      rated: false,
      result: null,
    },
  },
  {
    // T4/T5: da un replay, finale di corsa, cubo «ignoto». Le stime sono aperte.
    id: 'b0a4d000-0000-4000-8000-000000000003',
    capturedFrom: 'replay',
    createdAt: QUANDO(2, 9, 12),
    clocksAtCapture: null,
    position: POS_FINALE,
    origin: {
      matchId: '3a7c0000-0000-4000-8000-000000000003',
      gameNumber: 1,
      ply: 41,
      status: 'finished',
      players: { W: giocatore(MIO_ID_FINTO, 'Io', 1500), B: giocatore('a11c0000-0000-4000-8000-000000000002', 'Marco_bg', 1612) },
      timeControl: BLITZ_3_10,
      tournamentMatchId: null,
      serviceOrdered: false,
      rated: true,
      result: esito({ winner: 'B', winMagnitude: 1, score: { W: 0, B: 1 } }),
    },
  },
  {
    // Il medio gioco della T5 a partita finita, col tiro in mano: stime aperte con le mosse candidate.
    id: 'b0a4d000-0000-4000-8000-000000000004',
    capturedFrom: 'spectator',
    createdAt: QUANDO(4, 20, 5),
    clocksAtCapture: { W: 102_000, B: 125_000 },
    position: POS_MEDIO_GIOCO,
    origin: {
      matchId: '3a7c0000-0000-4000-8000-000000000004',
      gameNumber: 2,
      ply: 14,
      status: 'finished',
      players: { W: giocatore('a11c0000-0000-4000-8000-000000000001', 'Giulia R.', 1655), B: giocatore('a11c0000-0000-4000-8000-000000000002', 'Marco_bg', 1612) },
      timeControl: BLITZ_3_10,
      tournamentMatchId: 'tm-12',
      serviceOrdered: true,
      rated: false,
      result: esito({ winner: 'B', winMagnitude: 1, score: { W: 3, B: 5 } }),
    },
  },
  {
    // Un raddoppio offerto: la scheda dice che l'offerta si toglie nella partita.
    id: 'b0a4d000-0000-4000-8000-000000000005',
    capturedFrom: 'player',
    createdAt: QUANDO(5, 11, 40),
    clocksAtCapture: { W: 88_000, B: 61_000 },
    position: POS_RADDOPPIO,
    origin: {
      matchId: '3a7c0000-0000-4000-8000-000000000005',
      gameNumber: 2,
      ply: 13,
      status: 'finished',
      // Un account cancellato: `userId`, `displayName` ed `elo` a `null` (ADR-0191 §3) — la riga dice «giocatore non piu' presente».
      players: { W: { userId: null, displayName: null, avatarUrl: null, elo: null, isBot: false }, B: giocatore(MIO_ID_FINTO, 'Io', 1500) },
      timeControl: BLITZ_1_5,
      tournamentMatchId: null,
      serviceOrdered: false,
      rated: false,
      result: esito({ winner: 'W', winMagnitude: 2, score: { W: 5, B: 2 } }),
    },
  },
];

/** La comunita' finta (T5: «Salvato 4 volte · giocato 3: Bianco 2, Nero 1»). */
export function comunitaFinta(position: PositionView): BoardCommunity {
  return position.id === 'b05e0000-0000-4000-8000-000000000001'
    ? { saves: 4, plays: 3, whiteWins: 2, blackWins: 1 }
    : { saves: 1, plays: 0, whiteWins: 0, blackWins: 0 };
}

/**
 * Le stime del bot, finte: chiuse finche' la partita d'origine e' viva (L-2746 (a)), aperte dopo; una posizione di fine
 * gioco non si stima (`terminal`). Le mosse candidate sono quelle del 6-2 del Nero della tavola T5, scritte come mosse (la
 * notazione la fa la TMA).
 */
export function analisiFinta(board: BoardView): AnalysisState {
  if (board.origin.status === 'live') return { kind: 'closed', reason: 'source_live' };
  if (board.position.phase === 'gameOver') return { kind: 'closed', reason: 'terminal' };
  const nero = board.position.turn === 'B';
  const conTiro = board.position.roll !== null;
  return {
    kind: 'open',
    analysis: {
      engine: 'eval-2',
      evaluatorVersion: 'eval-2.0',
      winProb: nero ? { W: 0.59, B: 0.41 } : { W: 0.5, B: 0.5 },
      shots: { player: board.position.turn, count: 11 },
      ...(conTiro
        ? {
            candidates: [
              {
                moves: [
                  { from: 'bar', to: 1, die: 2 },
                  { from: 11, to: 17, die: 6 },
                ],
                winProb: { W: 0.59, B: 0.41 },
              },
              {
                moves: [
                  { from: 'bar', to: 1, die: 2 },
                  { from: 16, to: 22, die: 6 },
                ],
                winProb: { W: 0.61, B: 0.39 },
              },
              {
                moves: [
                  { from: 'bar', to: 1, die: 2 },
                  { from: 1, to: 7, die: 6 },
                ],
                winProb: { W: 0.63, B: 0.37 },
              },
            ],
          }
        : {}),
    },
  };
}

/**
 * Lo scenario finto: la partenza e le righe vere per quel board (ADR-0191 §6). ⛔ NON e' `scenarioDa`: quella e' una funzione
 * del SERVER (un campo di `GET /positions/:id`), e il client non ne tiene una copia. Qui serve solo a far vedere al foglio le
 * quattro righe, una per quando vale.
 */
export function scenarioFintoDi(position: PositionView): ScenarioView {
  const differences: ScenarioView['differences'] = [];
  if (position.roll !== null) differences.push('roll_removed');
  if (position.phase === 'cubeOffered') differences.push('offer_removed');
  if (!position.crawford) differences.push('cube_off');
  if (position.match.length > 1) differences.push('score_not_played');
  return {
    v: 1,
    // `{points, bar, off, turn}` e basta (`ScenarioSchema.start`): niente tiro, niente fase, niente cubo.
    start: { points: position.points, bar: position.bar, off: position.off, turn: position.turn },
    differences,
  };
}

/** Gli amici finti del foglio della sfida (T6): online in cima, poi per nome. */
export interface AmicoFinto {
  userId: string;
  nome: string;
  online: boolean;
}

export const AMICI_FINTI: AmicoFinto[] = [
  { userId: 'a11c0000-0000-4000-8000-000000000001', nome: 'Giulia R.', online: true },
  { userId: 'a11c0000-0000-4000-8000-000000000002', nome: 'Marco_bg', online: true },
  { userId: 'a11c0000-0000-4000-8000-000000000003', nome: 'Sara_77', online: false },
];

/**
 * Le modalita' di tempo finte: la `gameModeKey` e l'etichetta DEL SERVER (L-2523), come le manda
 * `GET /v1/games/:gameId/match-modes` filtrate a 1 punto. Il «Blitz» del web (180/10) e' il `rapid` del server.
 */
export const MODALITA_TEMPO_FINTE: ModalitaTempoSfida[] = [
  { gameModeKey: 'backgammon-1pt-blitz', label: 'Partita secca · blitz (60s +5)' },
  { gameModeKey: 'backgammon-1pt-rapid', label: 'Partita secca · rapid (180s +10)' },
  { gameModeKey: 'backgammon-1pt-classic', label: 'Partita secca · classic (600s +15)' },
];
